/**
 * Čistá logika filtrování/řazení navigačního stromu (E11-US1/US2) — bez
 * závislosti na Reactu, snadno testovatelná odděleně od `NavigationTree.tsx`.
 *
 * Řazení: tabulky abecedně (kap. 9 otevřená otázka — zvoleno jako
 * deterministický výchozí stav), sloupce dle pořadí v DB (`ordinal`), protože
 * to odpovídá tomu, jak je vidí i canvas (Epic 6) a jak je zvyklý na ně
 * uživatel z DB nástrojů.
 */
import type { ColumnModel, TableModel } from '@bits2schema/shared';

export interface TreeTableEntry {
  table: TableModel;
  /** Sloupce k vykreslení pod tabulkou — při aktivním filtru jen ty odpovídající dotazu. */
  columns: ColumnModel[];
}

function sortColumns(columns: ColumnModel[]): ColumnModel[] {
  return [...columns].sort((a, b) => a.ordinal - b.ordinal);
}

/**
 * Tabulka zůstává viditelná, pokud odpovídá dotazu její název (pak se
 * zobrazí všechny její sloupce), nebo odpovídá alespoň jeden sloupec
 * (pak se zobrazí jen odpovídající sloupce) — viz epic-11 kap. 2/7 (E11-US2).
 */
export function buildTreeEntries(tables: TableModel[], query: string): TreeTableEntry[] {
  const sorted = [...tables].sort((a, b) => a.name.localeCompare(b.name));
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return sorted.map((table) => ({ table, columns: sortColumns(table.columns) }));
  }

  const entries: TreeTableEntry[] = [];
  for (const table of sorted) {
    const columns = sortColumns(table.columns);
    if (table.name.toLowerCase().includes(normalizedQuery)) {
      entries.push({ table, columns });
      continue;
    }
    const matchingColumns = columns.filter((column) => column.name.toLowerCase().includes(normalizedQuery));
    if (matchingColumns.length > 0) entries.push({ table, columns: matchingColumns });
  }
  return entries;
}
